import { useScrollPaddingTopForType } from 'atoms/scrollPaddingTopAtom';
import { useEffect, useRef, useState } from 'react';
import { AnchorNavigation } from 'types/studio/studio';

export const useLinkSectionsIntersectionObserver = (
  anchors: AnchorNavigation['anchors'],
  offset: number = 0
) => {
  const [activeLink, setActiveLink] = useState('');
  const [scrollIsActive, setScrollIsActive] = useState(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const navigationBottom = useScrollPaddingTopForType(null, 'raw') + offset;

  const handleScrollStop = () => {
    setScrollIsActive(false);
  };

  const setActiveLinkWithScroll = (link: string) => {
    clearTimeout(clearTimerRef.current);
    setScrollIsActive(true);
    setActiveLink(link);

    const section = document.getElementById(link);

    if (section) {
      const { top } = section.getBoundingClientRect();

      window.scrollTo({
        top: top + window.scrollY - navigationBottom,
        behavior: 'smooth'
      });
    }

    clearTimerRef.current = setTimeout(handleScrollStop, 1500);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let hasActiveLink = false;

        entries.forEach((entry) => {
          if (entry.isIntersecting && !scrollIsActive) {
            setActiveLink(entry.target.id);
            hasActiveLink = true;
          }
        });

        if (!hasActiveLink && !scrollIsActive) {
          setActiveLink('');
        }
      },
      { rootMargin: '-50% 0% -50% 0%', threshold: 0 }
    );

    setTimeout(() => {
      anchors.forEach((anchor) => {
        const element = document.getElementById(anchor.anchorLink);
        if (element) {
          observer.observe(element);
        }
      });
    }, 1000);

    return () => {
      anchors.forEach((anchor) => {
        const element = document.getElementById(anchor.anchorLink);
        if (element) observer.unobserve(element);
      });
    };
  }, [anchors, scrollIsActive]);

  return { activeLink, setActiveLinkWithScroll };
};
